import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MotifButton } from "@ey-xd/motif-react";
import {
  useLazyGetManualPaymentClaimItemsQuery,
  useGenerateManualPaymentMutation,
} from "@/services/paymentStatus/paymentStatusReducer";
import Table from "@/components/Table/table";
import { useColumnCalimItemsDefs } from "./ClaimItemColumnDefs";
import { useColumnManualDefs } from "./ManualPaymentColumnDefs";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { customTableLoadingData } from "@/components/Table/CustomPaginationConstants";
import { formatAmountWithCommas } from "@/utility/common";
import { ActiveTabsOrder } from "@/menuData";
import { useDispatch } from "react-redux";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
const ClaimItems = () => {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { control, handleSubmit, getValues, setValue, watch, reset } =
    useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDiscardVisible, setIsDiscardVisible] = useState(false);
  const claimReferenceId = getValues("claimReferenceId");
  const claimId = getValues("claimId");
  const distributionId = getValues("distribution");
  const generatePaymentBy = parseInt(getValues("paymentOption"), 10);
  const subAccountId = getValues("subAccount");
  const paymentAmount = getValues("paymentAmount");
  const unitId = getValues("unit");
  const accountUnit = getValues("accountUnit");
  const unitDecimalPlace = getValues("unitDecimalPlace");
  const exchangeRate = getValues("exchangeRate");
  const claimItemsId = watch("claimItems");
  const generateClaimItemIds = watch("generateClaimItemIds");
  const generatePaymentItems = watch("generatePaymentItems");
  const tableLoadingData = customTableLoadingData;
  const addedClaimItemIds = watch("addedClaimItemIds") || [];
  const [totalResolvedAmt, setTotalResolvedAmt] = useState(0);
  const [
    getMaualPaymentData,
    { isFetching, data: manualClaimItemData, error },
  ] = useLazyGetManualPaymentClaimItemsQuery();
  const [generateManualPayment] = useGenerateManualPaymentMutation();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: true }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch]);
  const requestBody = useMemo(
    () => ({
      claimId,
      distributionId,
      generatePaymentBy,
      subAccountId,
      paymentAmount,
      unitId,
      exchangeRate,
    }),
    [
      claimId,
      distributionId,
      generatePaymentBy,
      subAccountId,
      paymentAmount,
      unitId,
      exchangeRate,
    ]
  );

  const updateClaimItems = useCallback(
    async (requestParam: any) => {
      const existingManualPaymentItems =
        getValues("generatePaymentItems") || [];
      await Promise.all([getMaualPaymentData(requestParam).unwrap()])
        .then((data: any) => {
          if (data && data.length > 0) {
            const paymentData = data[0].generatePaymentItems;
            if (paymentData.length === 0) {
              setValue("generatePaymentItems", []);
            }
            let updatedPaymentData = [...paymentData];
            if (generatePaymentBy === 1) {
              updatedPaymentData = paymentData.map((item: any) => {
                const existingItem = existingManualPaymentItems.find(
                  (existing: any) => existing.claimItemId === item.claimItemId
                );
                return existingItem
                  ? { ...item, settlementAmount: existingItem.settlementAmount }
                  : item;
              });
            }
            let modifiedPaymentData = [...updatedPaymentData];
            modifiedPaymentData = modifiedPaymentData.map((item: any) => {
              return {
                ...item,
                settlementAmount: item.settlementAmount
                  ? parseFloat(
                      item.settlementAmount?.toFixed(
                        unitDecimalPlace || unitDecimalPlace === 0
                          ? unitDecimalPlace
                          : 8
                      )
                    )
                  : item.settlementAmount,
              };
            });
            setValue("generatePaymentItems", modifiedPaymentData);
            const total = updatedPaymentData.reduce(
              (sum: any, item: any) =>
                sum + (parseFloat(item.settlementAmount) || 0),
              0
            );
            let formattedTotal = total;
            if (unitDecimalPlace !== null) {
              formattedTotal = formatAmountWithCommas(total, unitDecimalPlace);
            }
            if (accountUnit) {
              formattedTotal += ` ${accountUnit}`;
            }
            setTotalResolvedAmt(formattedTotal);
          } else {
            setValue("claimItems", []);
            setValue("generateClaimItemIds", []);
            setValue("addedClaimItemIds", []);
            setValue("generatePaymentItems", []);
            setTotalResolvedAmt(0);
          }
        })
        .catch(() => {
          setValue("claimItems", []);
          setValue("generateClaimItemIds", []);
          setValue("addedClaimItemIds", []);
          setValue("generatePaymentItems", []);
          setTotalResolvedAmt(0);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMaualPaymentData, setValue]
  );

  useEffect(() => {
    const body = {
      ...requestBody,
      claimItemIds: addedClaimItemIds,
    };
    updateClaimItems(body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateClaimItems, requestBody]);

  const confirmationModalCancelHandler = () => {
    setIsDiscardVisible(false);
    dispatch(setDiscardEditMode({ editMode: true }));
  };

  const confirmationModalHandler = () => {
    reset();
    navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.PAYMENT}`);
    setIsDiscardVisible(false);
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    const currentClaimItems = getValues("claimItems") || [];
    const currentManualPaymentItems = getValues("generateClaimItemIds") || [];
    if (checked) {
      if (!currentManualPaymentItems.includes(id)) {
        setValue("generateClaimItemIds", [...currentManualPaymentItems, id]);
      }
      setValue("claimItems", [...currentClaimItems, id]);
    } else {
      setValue(
        "generateClaimItemIds",
        currentManualPaymentItems.filter((item: string) => item !== id)
      );
      setValue(
        "claimItems",
        currentClaimItems.filter((item: string) => item !== id)
      );
    }
  };
  const handleDelete = (id: string) => {
    const currentClaimItems = getValues("generateClaimItemIds") || [];
    const currentManualPaymentItems = getValues("generatePaymentItems") || [];
    const updatedClaimItemsId = currentClaimItems.filter(
      (item: string) => item !== id
    );
    const updatedPaymentItems = currentManualPaymentItems.filter(
      (item: any) => item.claimItemId !== id
    );
    const body = {
      ...requestBody,
      claimItemIds: updatedClaimItemsId,
    };
    setValue("generatePaymentItems", updatedPaymentItems);
    updateClaimItems(body);
    setValue("generateClaimItemIds", updatedClaimItemsId);
    setValue("addedClaimItemIds", updatedClaimItemsId);
  };

  const handleValueChange = (rowIndex: number, value: string) => {
    const updatedPaymentItems = [...generatePaymentItems];
    updatedPaymentItems[rowIndex] = {
      ...updatedPaymentItems[rowIndex],
      settlementAmount: parseFloat(value) || 0,
    };
    setValue("generatePaymentItems", updatedPaymentItems);
    const total = updatedPaymentItems.reduce(
      (sum: any, item: any) => sum + (parseFloat(item.settlementAmount) || 0),
      0
    );
    let formattedTotal = total;
    if (unitDecimalPlace !== null) {
      formattedTotal = formatAmountWithCommas(total, unitDecimalPlace);
    }
    if (accountUnit) {
      formattedTotal += ` ${accountUnit}`;
    }
    setTotalResolvedAmt(formattedTotal);
  };

  const columnCalimItemsDefs = useColumnCalimItemsDefs(
    control,
    handleCheckboxChange,
    unitDecimalPlace,
    accountUnit
  );
  const columnManualPaymentDefs = useColumnManualDefs(
    control,
    generatePaymentBy.toString(),
    handleDelete,
    handleValueChange,
    unitDecimalPlace,
    accountUnit
  );

  const addDataToPayment = () => {
    const body = {
      ...requestBody,
      claimItemIds: generateClaimItemIds,
    };
    setValue("addedClaimItemIds", [...generateClaimItemIds]);
    updateClaimItems(body);
    setValue("claimItems", []);
  };

  const onSubmit = async (data: any) => {
    const postBody = {
      caseId,
      creditorId: data.creditorId,
      subAccountId,
      unitId: unitId ? unitId : null,
      distributionId: distributionId,
      generatePaymentItems: data.generatePaymentItems,
      prefix:data.prefix?Number(data.prefix):null,
      exchangeRate:data?.paymentOption!=="1"?data.exchangeRate:null,
      amount: data?.paymentOption!=="1"?parseFloat(data.paymentAmount):null,
    };
    dispatch(setDiscardEditMode({ editMode: false }));
    setIsLoading(true);
    await generateManualPayment(postBody).then((response: any) => {
      if (response.data) {
        setIsLoading(false);
        navigate(`/case/${caseId}?activeTab=${ActiveTabsOrder.PAYMENT}`);
      } else {
        if (response.error.data?.title) {
          setErrorMessage(response.error?.data?.title);
        } else {
          setErrorMessage(
            response.error?.data?.message ||
              t("global.globalNotifications.unExpectedError")
          );
        }
        setIsLoading(false);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Table
        columnDefs={columnCalimItemsDefs}
        cellRenderers={
          isFetching
            ? []
            : manualClaimItemData?.claimItems
            ? manualClaimItemData?.claimItems
            : []
        }
        TitleComplement={() => (
          <MotifButton
            variant="secondary"
            type="button"
            disabled={claimItemsId.length === 0}
            onClick={addDataToPayment}
          >
            {t("pages.paymentStatus.paymentSetup.claimItems.addToPayment")}
          </MotifButton>
        )}
        title={t("pages.paymentStatus.paymentSetup.claimItems.title", {
          claimReferenceId: claimReferenceId,
        })}
        description={t(
          "pages.paymentStatus.paymentSetup.claimItems.description"
        )}
        pagination={false}
        overlayNoRowsTemplate={
          isFetching
            ? t(tableLoadingData)
            : error
            ? t("pages.paymentStatus.somethingWentWrong")
            : t("pages.paymentStatus.noRecord")
        }
      />

      <div className="mar-t-20">
        <Table
          columnDefs={columnManualPaymentDefs}
          cellRenderers={
            isFetching
              ? []
              : manualClaimItemData?.generatePaymentItems
              ? manualClaimItemData?.generatePaymentItems
              : []
          }
          pinnedBottomRowData={
            manualClaimItemData &&
            manualClaimItemData?.generatePaymentItems.length > 0
              ? [
                  {
                    claimItemReference: t(
                      "pages.paymentStatus.paymentSetup.manualPayment.total"
                    ),
                    settlementAmount: totalResolvedAmt,
                  },
                ]
              : []
          }
          TitleComplement={() => <></>}
          title={t("pages.paymentStatus.paymentSetup.manualPayment.title", {
            claimReferenceId: claimReferenceId,
          })}
          description={t(
            "pages.paymentStatus.paymentSetup.manualPayment.description"
          )}
          pagination={false}
          overlayNoRowsTemplate={
            isFetching
              ? t(tableLoadingData)
              : error
              ? t("pages.paymentStatus.somethingWentWrong")
              : t("pages.paymentStatus.noRecord")
          }
        />
      </div>
      <div className="mar-t-20">
        {errorMessage && (
          <NotificationGrid show={true} message={errorMessage} type={"error"} />
        )}
      </div>

      <div className="display-flex justify-content-end mar-t-20 button-container sticky-footer">
        <div className="pad-r-10">
          <MotifButton
            type="button"
            variant={"secondary"}
            onClick={() => {
              dispatch(setDiscardEditMode({ editMode: false }));
              setIsDiscardVisible(true);
            }}
          >
            {t("global.globalActions.cancel")}
          </MotifButton>
        </div>
        <div>
          <MotifButton
            variant="primary"
            type={isLoading ? "loader" : "submit"}
            disabled={
              isLoading ||
              !manualClaimItemData ||
              manualClaimItemData?.generatePaymentItems.length === 0
            }
          >
            {t("pages.paymentStatus.paymentSetup.details.generatePayment")}
          </MotifButton>
        </div>
      </div>
      <ConfirmationModal
        isVisible={isDiscardVisible}
        setIsVisible={setIsDiscardVisible}
        cancelBtnApproveHandler={confirmationModalHandler}
        cancelBtnRejectHandler={confirmationModalCancelHandler}
      />
    </form>
  );
};

export default ClaimItems;
